import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, X, User, Phone, Car, Building2, FileText, Calendar, ChevronRight, AlertCircle } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn, apiRequest, formatPhone } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export const GlobalSearch = ({ className, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const debounceRef = useRef(null);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      let params = `q=${encodeURIComponent(searchQuery)}&limit=15`;
      if (!isAdmin() && user?.id) {
        params += `&cre_user_id=${user.id}`;
      }
      
      const data = await apiRequest(`/search?${params}`);
      setResults(data || []);
    } catch (error) {
      console.error("Search failed:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedIndex === results.length) {
          // View All Results
          handleViewAll();
        } else if (selectedIndex >= 0) {
          handleSelect(results[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        setIsOpen(false);
        setQuery("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, results, selectedIndex, query]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (resultsRef.current && !resultsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    setIsOpen(false);
    setQuery("");
    
    // Navigate based on type and status
    if (item.type === "customer") {
      // Navigate to customer profile directly
      navigate(`/customers/${item.id}`);
    } else if (item.type === "lead") {
      navigate("/leads");
    } else if (item.type === "booking") {
      navigate("/bookings");
    } else if (item.type === "complaint") {
      navigate("/complaints");
    }
  };

  const handleViewAll = () => {
    if (query.length >= 2) {
      setIsOpen(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setQuery("");
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "customer": return <User className="w-4 h-4 text-blue-600" />;
      case "lead": return <User className="w-4 h-4 text-green-600" />;
      case "booking": return <Calendar className="w-4 h-4 text-purple-600" />;
      case "complaint": return <AlertCircle className="w-4 h-4 text-red-600" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      customer: "bg-blue-100 text-blue-800",
      lead: "bg-green-100 text-green-800",
      booking: "bg-purple-100 text-purple-800",
      complaint: "bg-red-100 text-red-800"
    };
    return <Badge className={`${styles[type] || "bg-gray-100 text-gray-800"} text-[10px] uppercase`}>
      {type}
    </Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      due: "bg-yellow-100 text-yellow-800",
      overdue: "bg-orange-100 text-orange-800",
      delayed: "bg-blue-100 text-blue-800",
      no_show: "bg-red-100 text-red-800",
      active: "bg-green-100 text-green-800",
      new_lead: "bg-green-100 text-green-800",
      follow_up: "bg-purple-100 text-purple-800",
      booked: "bg-emerald-100 text-emerald-800",
      scheduled: "bg-blue-100 text-blue-800",
      open: "bg-red-100 text-red-800"
    };
    if (!status) return null;
    return <Badge className={`${styles[status] || "bg-gray-100 text-gray-800"} text-[10px]`}>
      {status?.replace("_", " ")}
    </Badge>;
  };

  return (
    <div className={cn("relative", className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={isMobile ? "Search..." : "Search name, phone, vehicle, RG NOM..."}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          className={cn(
            "pl-9 pr-8 bg-gray-50 border-gray-200 focus:bg-white",
            isMobile ? "h-9 text-sm" : "h-10 w-full min-w-[280px] lg:min-w-[360px]"
          )}
          data-testid="global-search-input"
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={() => {
              setQuery("");
              setResults([]);
              inputRef.current?.focus();
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div
          ref={resultsRef}
          className={cn(
            "absolute top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 max-h-[70vh] overflow-y-auto z-[100]",
            isMobile ? "left-0 right-0" : "left-0 right-0 min-w-[360px]"
          )}
          data-testid="search-results-dropdown"
        >
          {loading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#ED1C24] mx-auto mb-2"></div>
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {query.length < 2 ? "Type at least 2 characters" : "No results found"}
            </div>
          ) : (
            <div className="py-1">
              {/* Results */}
              {results.map((item, index) => (
                <button
                  key={`${item.type}-${item.id}`}
                  className={cn(
                    "w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-start gap-3 transition-colors border-b border-gray-50 last:border-0",
                    selectedIndex === index && "bg-gray-100"
                  )}
                  onClick={() => handleSelect(item)}
                  data-testid={`search-result-${item.type}-${item.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">{getTypeIcon(item.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{item.name}</span>
                      {getTypeBadge(item.type)}
                      {item.status && getStatusBadge(item.status)}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      {item.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {formatPhone(item.phone)}
                        </span>
                      )}
                      {item.vehicle && (
                        <span className="flex items-center gap-1 truncate">
                          <Car className="w-3 h-3" />
                          {item.vehicle}
                        </span>
                      )}
                      {item.vehicle_number && (
                        <span className="font-mono text-[10px] bg-gray-100 px-1 rounded">
                          {item.vehicle_number}
                        </span>
                      )}
                    </div>
                    {item.branch_name && (
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {item.branch_name}
                      </div>
                    )}
                  </div>
                </button>
              ))}
              
              {/* View All Results */}
              {results.length > 0 && (
                <button
                  className={cn(
                    "w-full px-3 py-3 text-left hover:bg-[#ED1C24]/5 flex items-center justify-between text-sm font-medium text-[#ED1C24] border-t border-gray-100",
                    selectedIndex === results.length && "bg-[#ED1C24]/10"
                  )}
                  onClick={handleViewAll}
                  data-testid="view-all-results-btn"
                >
                  <span>View All Results for "{query}"</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
